// Typed event bus — §12.3 / DR-09. The mutation mechanism for cross-boundary
// actions. The Zustand store is the source of truth; events are how surfaces
// request changes without writing to the store directly (§12.4).
//
// Exposed to workspace remotes via Module Federation so they dispatch through
// identical primitives (Surface Interface Contract, §7.2 useShellEvent()).

import type {
  ShellEvent,
  ShellEventBus,
  ShellEventHandler,
  ShellEventType,
  Unsubscribe,
} from '@growthaxl/web-foundation';
import { SHELL_EVENT_BUS_WINDOW_KEY } from '../../constants/storageKeys';

class TypedEventBus implements ShellEventBus {
  private handlers = new Set<ShellEventHandler>();

  dispatch(event: ShellEvent): void {
    for (const handler of this.handlers) {
      try {
        handler(event);
      } catch (err) {
        console.error('[shell-event-bus] handler error', err);
      }
    }
  }

  subscribe(handler: ShellEventHandler): Unsubscribe {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  on<T extends ShellEventType>(
    type: T,
    handler: (event: Extract<ShellEvent, { type: T }>) => void
  ): Unsubscribe {
    return this.subscribe(event => {
      if (event.type === type) handler(event as Extract<ShellEvent, { type: T }>);
    });
  }
}

// Use window global to ensure true singleton across all module instances / HMR reloads
declare global {
  interface Window {
    [SHELL_EVENT_BUS_WINDOW_KEY]?: ShellEventBus;
  }
}

function getOrCreateEventBus(): ShellEventBus {
  if (typeof window !== 'undefined') {
    if (!window[SHELL_EVENT_BUS_WINDOW_KEY]) {
      window[SHELL_EVENT_BUS_WINDOW_KEY] = new TypedEventBus();
    }
    return window[SHELL_EVENT_BUS_WINDOW_KEY];
  }
  return new TypedEventBus();
}

/** Singleton shell event bus shared across the host and all remotes. */
export const shellEventBus: ShellEventBus = getOrCreateEventBus();
